import {
  type CellData,
  type Column,
  type ColumnDef,
  type ColumnHelper,
  type Table as CoreTable,
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  tableFeatures as defineTableFeatures,
  filterFn_arrHas,
  type RowData,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  type TableFeatures,
} from '@tanstack/react-table';

export interface TableColumnMeta<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData,
> {
  /** Specifies whether the contents in the column should be right-aligned. */
  rightAlign?: boolean;
  /** Text to display as a tooltip for the column. */
  tooltip?: string;
  /** Defines the filter variant. */
  filterVariant?: 'select';
  /** Enables searching of filter options. Defaults to true. */
  showSearchInFilter?: boolean;
  filterOptionSortFn?: (a: TValue, b: TValue) => number;
  /** Extracts a displayable value from a filter option. */
  filterOptionValueAccessor?: (
    value: TValue,
  ) => string | number | undefined | null;
  /** Returns the searchable label for a filter option. */
  filterOptionLabel?: (
    value: TValue,
    column: Column<TFeatures, TData, TValue>,
  ) => string | null | undefined;
  /** Renders a filter option. */
  renderFilterOption?: (
    value: TValue,
    column: Column<TFeatures, TData, TValue>,
  ) => React.ReactNode;
  /** Hides the sorting control for this column. */
  hideSorting?: boolean;
  /** Custom title for the filter. */
  filterTitleOverride?: string;
  /** Custom placeholder for the filter. */
  filterPlaceholder?: string;
}

declare module '@tanstack/table-core' {
  interface ColumnMeta<
    TFeatures extends TableFeatures,
    TData extends RowData,
    TValue extends CellData = CellData,
  > extends TableColumnMeta<TFeatures, TData, TValue> {}
}

export const kkTableFeatures = defineTableFeatures({
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  filterFns: { arrHas: filterFn_arrHas },
});

export const createTableColumnHelper = <TData extends RowData>() =>
  createColumnHelper<typeof kkTableFeatures, TData>();

export type TableColumnDef<TData extends RowData, TValue = unknown> = ColumnDef<
  typeof kkTableFeatures,
  TData,
  TValue
>;

export type TableColumnHelper<TData extends RowData> = ColumnHelper<
  typeof kkTableFeatures,
  TData
>;

export type TableInstance<TData extends RowData> = CoreTable<
  typeof kkTableFeatures,
  TData
>;
